import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getPlatformSettings, updatePlatformSettings } from "../../lib/api";
import { format, parseISO } from "date-fns";

// Currency symbols mapping
const CURRENCY_SYMBOLS = {
  GBP: "£",
  EUR: "€",
  USD: "$",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
};

// Default settings
const DEFAULT_SETTINGS = {
  date_format: "DD/MM/YYYY",
  time_format: "24-hour",
  timezone: "UTC",
  currency: "GBP",
  number_format: "1,234.56",
  theme_mode: "dark",
  primary_color: null,
  secondary_color: null,
  high_contrast_mode: false,
};

const PlatformSettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  loading: true,
  error: null,
  updateSettings: async () => {},
  formatDate: () => "",
  formatTime: () => "",
  formatCurrency: () => "",
  formatNumber: () => "",
  getCurrencySymbol: () => "£",
});

export function PlatformSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getPlatformSettings();
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });
      } catch (err) {
        console.error("Failed to load platform settings:", err);
        // Use defaults on error
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Update settings
  const handleUpdateSettings = useCallback(async (newSettings) => {
    try {
      const response = await updatePlatformSettings(newSettings);
      setSettings({ ...DEFAULT_SETTINGS, ...response.data });
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Format date according to settings
  const formatDate = useCallback((dateInput) => {
    if (!dateInput) return "";
    
    try {
      const date = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
      
      switch (settings.date_format) {
        case "DD/MM/YYYY":
          return format(date, "dd/MM/yyyy");
        case "MM/DD/YYYY":
          return format(date, "MM/dd/yyyy");
        case "YYYY-MM-DD":
          return format(date, "yyyy-MM-dd");
        default:
          return format(date, "dd/MM/yyyy");
      }
    } catch {
      return String(dateInput);
    }
  }, [settings.date_format]);

  // Format time according to settings
  const formatTime = useCallback((timeInput) => {
    if (!timeInput) return "";
    
    try {
      // Handle HH:mm format string
      if (typeof timeInput === "string" && timeInput.includes(":")) {
        const [hours, minutes] = timeInput.split(":").map(Number);
        
        if (settings.time_format === "12-hour") {
          const period = hours >= 12 ? "PM" : "AM";
          const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
          return `${hour12}:${minutes.toString().padStart(2, "0")} ${period}`;
        }
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      }
      
      // Handle Date object
      const date = typeof timeInput === "string" ? parseISO(timeInput) : timeInput;
      return settings.time_format === "12-hour"
        ? format(date, "h:mm a")
        : format(date, "HH:mm");
    } catch {
      return String(timeInput);
    }
  }, [settings.time_format]);

  // Get currency symbol
  const getCurrencySymbol = useCallback(() => {
    return CURRENCY_SYMBOLS[settings.currency] || "£";
  }, [settings.currency]);

  // Format number according to settings
  const formatNumber = useCallback((value) => {
    if (value === null || value === undefined) return "";
    
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    
    switch (settings.number_format) {
      case "1.234,56": // European style
        return num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      case "1 234,56": // French style
        return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      case "1,234.56": // US/UK style
      default:
        return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }, [settings.number_format]);

  // Format currency
  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) return "";
    return `${getCurrencySymbol()}${formatNumber(value)}`;
  }, [getCurrencySymbol, formatNumber]);

  const value = {
    settings,
    loading,
    error,
    updateSettings: handleUpdateSettings,
    formatDate,
    formatTime,
    formatCurrency,
    formatNumber,
    getCurrencySymbol,
  };

  return (
    <PlatformSettingsContext.Provider value={value}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

export const usePlatformSettings = () => {
  const context = useContext(PlatformSettingsContext);
  if (context === undefined) {
    throw new Error("usePlatformSettings must be used within a PlatformSettingsProvider");
  }
  return context;
};
